/**
 * Vue projet : un espace HTML/CSS libre, hors parcours de lecon.
 *
 * Le code de l'eleve est enregistre dans la configuration du projet plutot que
 * dans une reponse de lecon, d'ou l'enregistrement propre a cette vue.
 */

import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { message } from 'antd';

import { TProject } from '../../models/project/project';
import { HtmlCssConfig } from '../../models/htmlcss/types';
import { useProjectApi } from '../../api/projects/projects';

import { HtmlCssWorkspace } from './core/types';
import { normalizeConfigContent } from './core/config';
import { deserializeWorkspace, serializeWorkspace, workspacesAreEqual } from './core/workspace';
import HtmlCssStudent, { HtmlCssStudentRef } from './HtmlCssStudent';

interface HtmlCssProjectProps {
  project: TProject;
  enableEditCode: boolean;
}

export interface HtmlCssProjectRef {
  save: () => Promise<void>;
}

const HtmlCssProject = forwardRef<HtmlCssProjectRef, HtmlCssProjectProps>(
  ({ project, enableEditCode }, ref) => {
    const { updateProject } = useProjectApi();
    const studentRef = useRef<HtmlCssStudentRef>(null);

    const configContent = useMemo(
      () => normalizeConfigContent((project.config as HtmlCssConfig | undefined)?.config),
      [project.config]
    );

    // Reference du dernier etat enregistre, pour ne pas reecrire a l'identique.
    const [savedWorkspace, setSavedWorkspace] = useState<HtmlCssWorkspace | null>(() =>
      deserializeWorkspace(configContent.adminWorkspace)
    );

    /**
     * La vue projet reutilise la vue lecon : on lui fournit une lecon
     * synthetique portant la configuration et la description du projet.
     */
    const lesson = useMemo(
      () =>
        ({
          id: project.id,
          name: project.name || 'Projet HTML/CSS',
          statement: project.description || '',
          lesson_order: 0,
          exercise_component: 'htmlcss',
          epic_id: 0,
          lesson_type_id: 0,
          config: { lesson_id: project.id, config: configContent },
        } as any),
      [project.id, project.name, project.description, configContent]
    );

    const save = async (): Promise<void> => {
      const current = studentRef.current?.getCurrentCode();
      if (!current) return;

      if (savedWorkspace && workspacesAreEqual(savedWorkspace, current)) {
        message.info('Aucune modification à enregistrer.');
        return;
      }

      try {
        await updateProject(project.id, {
          ...project,
          config: {
            lesson_id: project.id,
            config: { ...configContent, adminWorkspace: serializeWorkspace(current) },
          },
        } as TProject);

        setSavedWorkspace(current);
      } catch (error: any) {
        message.error(error?.response?.data ?? error?.message ?? 'Enregistrement impossible.');
        // L'erreur est propagee : l'appelant ne doit pas enchainer sur l'etape
        // suivante ni annoncer un succes alors que le travail n'est pas
        // enregistre.
        throw error;
      }
    };

    useImperativeHandle(ref, () => ({ save }));

    return (
      <HtmlCssStudent
        ref={studentRef}
        lesson={lesson}
        checkResult={() => {}}
        statement={project.description || ''}
        enableEditCode={enableEditCode}
        // L'enregistrement passe par `save()` ci-dessus, qui ecrit dans la
        // configuration du projet via l'API des projets.
        isProject
      />
    );
  }
);

HtmlCssProject.displayName = 'HtmlCssProject';

export default HtmlCssProject;
